import React, { useEffect, useRef, useState } from "react";
import "../assets/styles/CvDownload.scss";

type Role = "pm" | "po" | "ba";
type Lang = "az" | "en";
type Step = "closed" | "role" | "lang";

const ROLES: { key: Role; label: string }[] = [
  { key: "pm", label: "IT PM" },
  { key: "po", label: "PO" },
  { key: "ba", label: "IT BA" },
];

const ROLE_LABEL: Record<Role, string> = {
  pm: "IT PM",
  po: "PO",
  ba: "IT BA",
};

const CV_FILES: Record<Role, Record<Lang, string>> = {
  pm: { az: "/cv/it-pm-az.pdf", en: "/cv/it-pm-en.pdf" },
  po: { az: "/cv/po-az.pdf", en: "/cv/po-en.pdf" },
  ba: { az: "/cv/it-ba-az.pdf", en: "/cv/it-ba-en.pdf" },
};

const CV_FILENAMES: Record<Role, Record<Lang, string>> = {
  pm: {
    az: "Sarkhan Babayev IT PM AZ.pdf",
    en: "Sarkhan Babayev IT PM EN.pdf",
  },
  po: {
    az: "Sarkhan Babayev Product Owner AZ.pdf",
    en: "Sarkhan Babayev Product Owner EN.pdf",
  },
  ba: {
    az: "Sarkhan Babayev Business Analyst AZ.pdf",
    en: "Sarkhan Babayev Business Analyst EN.pdf",
  },
};

interface CvDownloadProps {
  variant: "button" | "row";
  className?: string;
}

function CvDownload({ variant, className }: CvDownloadProps) {
  const [step, setStep] = useState<Step>("closed");
  const [role, setRole] = useState<Role | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (step === "closed") return;

    function handlePointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [step]);

  function close() {
    setStep("closed");
    setRole(null);
  }

  function toggle() {
    setStep((s) => (s === "closed" ? "role" : "closed"));
    setRole(null);
  }

  function pickRole(r: Role) {
    setRole(r);
    setStep("lang");
  }

  function pickLang(lang: Lang) {
    if (!role) return;
    const url = CV_FILES[role][lang];
    const filename = CV_FILENAMES[role][lang];
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    close();
  }

  const triggerClassName =
    variant === "button"
      ? `btn btn-ghost cv-download-trigger${className ? ` ${className}` : ""}`
      : `contact-row contact-row-cv cv-download-trigger${
          className ? ` ${className}` : ""
        }`;

  return (
    <div className={`cv-download cv-download-${variant}`} ref={rootRef}>
      <button
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={step !== "closed"}
        onClick={toggle}
      >
        {variant === "row" ? (
          <>
            <span className="cr-name">CV yüklə</span>
            <span className="cr-sub">PDF</span>
            <span className="cr-arrow">↓</span>
          </>
        ) : (
          <>CV yüklə ↓</>
        )}
      </button>

      {step !== "closed" && (
        <div className="cv-download-panel" role="menu">
          {step === "role" && (
            <>
              <span className="cv-download-hint">Vəzifə seç</span>
              <div className="cv-download-options">
                {ROLES.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    role="menuitem"
                    className="cv-download-option"
                    onClick={() => pickRole(r.key)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {step === "lang" && role && (
            <>
              <div className="cv-download-subhead">
                <button
                  type="button"
                  className="cv-download-back"
                  onClick={() => setStep("role")}
                  aria-label="Geri"
                >
                  ←
                </button>
                <span className="cv-download-hint">{ROLE_LABEL[role]}</span>
              </div>
              <div className="cv-download-options">
                <button
                  type="button"
                  role="menuitem"
                  className="cv-download-option"
                  onClick={() => pickLang("az")}
                >
                  AZ
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="cv-download-option"
                  onClick={() => pickLang("en")}
                >
                  EN
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CvDownload;
